import {html, render} from '@isceco/widget-library2/external/lit'
import '@isceco/widget-library2/basic-elements/Title/Title'
import '@isceco/widget-library2/basic-elements/Form/Form'
import '@isceco/widget-library2/basic-elements/Dropdown/Dropdown'
import '@isceco/widget-library2/basic-elements/DateInput/DateInput'
import '@isceco/widget-library2/basic-elements/Popup/Popup'
import '@isceco/widget-library2/basic-elements/Button/Button'
import WebComponent from '../../WebComponent.js'
import GemeindeService from '../../services/GemeindeService.js'
import BerufService from '../../services/BerufService.js'
import MindestlohnService from '../../services/MindestlohnService.js'
import KontaktinformationService from '../../services/KontaktinformationService.js'
import ConfigUtil from '../../services/ConfigUtil.js'
import {mindestlohnrechnerStyles} from './styles.js'
import {renderResultNavigationBar, renderResultSection} from './resultRenderers.js'
import {buildGavKurzSummaryGroups} from './gavKurzSummary.js'
import {buildPdfSections as buildResultPdfSections, formatDateForPdf} from './pdfSectionBuilders.js'
import {toPlainText} from './pdfText.js'
import {renderRelevantLinks} from './relevantLinks.js'
import {DETAILS_TITLE_GAV_KURZ_I18N_KEY, formatResultPageTitle, getResultPageTitleKey} from './resultPageTitles.js'
import {
  BEWILLIGUNG_LINK_I18N_KEY,
  MIN_DAYS_BEFORE_BEGINN,
  RESULT_PAGE,
  formatGemeindeDropdownLabel,
  gemeindeOptionValue,
  resolveTrefferName,
  toIsoDate,
  todayPlusDays
} from './utils.js'

const ERROR_TITLE_I18N_KEY = 'mindestlohnrechner.error.title'
const DETAILS_VALIDATION_SELECT_CONTRACT_I18N_KEY = 'mindestlohnrechner.details.validation.selectContract'
const DETAILS_SALARY_WAIT_FOR_SELECTION_I18N_KEY = 'mindestlohnrechner.details.salary.waitForSelection'
const DETAILS_HINTS_TITLE_I18N_KEY = 'mindestlohnrechner.details.hintsTitle'
const DETAILS_HINT_PERSONALVERLEIH_TITLE_I18N_KEY = 'mindestlohnrechner.details.hints.personalverleih.title'
const DETAILS_HINT_PERSONALVERLEIH_TEXT_I18N_KEY = 'mindestlohnrechner.details.hints.personalverleih.text'
const DETAILS_HINT_RECHTLICHER_HINWEIS_TITLE_I18N_KEY = 'mindestlohnrechner.details.hints.rechtlicherHinweis.title'
const DETAILS_HINT_RECHTLICHER_HINWEIS_TEXT_I18N_KEY = 'mindestlohnrechner.details.hints.rechtlicherHinweis.text'

export default class Mindestlohnrechner extends WebComponent {
  gemeindeItems = []
  selectedEinsatzortGemeinde = null
  selectedBeginn = ''
  berufe = []
  berufItems = []
  berufeFilterKey = null
  berufeFilterActive = false
  isLoading = false
  resultTreffer = null
  hasSearched = false
  bewilligungUrl = null
  selectedTrefferKey = null
  expandedSections = {}
  noMatchContacts = []
  contractParitaetContacts = []
  lastSearchInput = null
  activeResultPage = null
  selectedContractMeta = null
  selectedContractNumber = null
  criteriaFields = []
  currentSelections = {}
  resultRow = null
  resultHash = null
  expandedHintSections = {}
  lastRequestData = null
  isLoadingCriteria = false
  isLoadingResult = false
  isPdfLoading = false
  formValues = null

  get translationFile() {
    return './views/Mindestlohnrechner/i18n.json'
  }

  connectedCallback() {
    super.connectedCallback()
    new ConfigUtil().retrieveConfiguration().then(config => {
      this.bewilligungUrl = config.bewilligungUrl || this.i18n.translate(BEWILLIGUNG_LINK_I18N_KEY)
      this.render()
    }).catch(() => {
      this.bewilligungUrl = this.i18n.translate(BEWILLIGUNG_LINK_I18N_KEY)
      this.render()
    })
    this.loadBerufe()
    this.render()
  }

  async loadBerufe() {
    const berufe = await new BerufService().list()
    if (berufe) {
      this.berufe = berufe
      this.berufeFilterActive = false
      this.berufeFilterKey = null
      this.updateBerufItems()
      this.render()
    }
  }

  // Restrict the Suchbegriffe to those with a matching GAV/NAV for the chosen Einsatzort + Beginn.
  // Falls back to the full list while either input is still missing.
  async refreshBerufeForSelection() {
    const gemeinde = this.selectedEinsatzortGemeinde
    const beginn = this.selectedBeginn
    if (!gemeinde || !beginn) {
      if (this.berufeFilterActive) {
        await this.loadBerufe()
      }
      return
    }
    const filterKey = `${gemeinde.plz}|${gemeinde.kanton}|${gemeinde.ort}|${beginn}`
    if (filterKey === this.berufeFilterKey) {
      return
    }
    this.berufeFilterKey = filterKey
    try {
      const berufe = await new BerufService().listMatching({
        einsatzortPlz: gemeinde.plz,
        einsatzortKanton: gemeinde.kanton,
        einsatzortOrt: gemeinde.ort,
        beginn
      })
      if (filterKey !== this.berufeFilterKey) {
        return
      }
      this.berufe = Array.isArray(berufe) ? berufe : []
      this.berufeFilterActive = true
      this.updateBerufItems()
      this.clearBerufSelectionIfInvalid()
      this.render()
    } catch {
      // Keep the previous list if the filtered lookup fails.
    }
  }

  clearBerufSelectionIfInvalid() {
    const dropdown = this.querySelector('isceco-dropdown[name="beruf"]')
    if (!dropdown) {
      return
    }
    const current = dropdown.value
    if (current && !this.berufItems.some(item => item.value === current)) {
      dropdown.value = ''
    }
  }

  updateBerufItems() {
    const groupedItems = new Map()
    this.berufe.forEach(beruf => {
      const id = Number(beruf.id)
      const name = this.getTranslatedBerufName(beruf) || ''
      if (!Number.isFinite(id) || !name.trim()) {
        return
      }
      const key = name.trim().toLowerCase()
      const existing = groupedItems.get(key)
      if (existing) {
        existing.berufIds.push(id)
        return
      }
      groupedItems.set(key, {
        value: String(id),
        berufIds: [id],
        name
      })
    })

    this.berufItems = Array.from(groupedItems.values())
      .map(item => ({
        ...item,
        berufIds: item.berufIds.filter(berufId => Number.isFinite(berufId) && berufId > 0)
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  getTranslatedBerufName(beruf) {
    const lang = getLanguage()
    if (lang === 'fr' && beruf.nameFr) { return beruf.nameFr }
    if (lang === 'it' && beruf.nameIt) { return beruf.nameIt }
    if (lang === 'en' && beruf.nameEn) { return beruf.nameEn }
    return beruf.nameDe
  }

  reload() {
    this.updateBerufItems()
    super.reload()
    this.refreshForCurrentLanguage()
  }

  async refreshForCurrentLanguage() {
    if (!this.hasSearched || !this.lastRequestData) {
      return
    }
    try {
      await this.refreshSearchForCurrentLanguage()
      if (this.selectedContractNumber) {
        await this.loadCriteria(this.selectedContractNumber, this.currentSelections)
      }
    } catch {
      // Keep existing data if reloading in a new language fails.
    }
  }

  async refreshSearchForCurrentLanguage() {
    const language = getLanguage()
    const requestData = { ...this.lastRequestData, language }
    this.lastRequestData = requestData
    const result = await new MindestlohnService().search(requestData)
    this.resultTreffer = Array.isArray(result) ? result : []
    if (this.lastSearchInput) {
      this.lastSearchInput = { ...this.lastSearchInput }
    }
    if (this.selectedContractNumber) {
      this.selectedContractMeta = this.resultTreffer
        .find(treffer => treffer.contractNumber === this.selectedContractNumber) || this.selectedContractMeta
    }
    this.render()
  }

  handleEinsatzortChange(e) {
    const val = e.detail?.value ?? e.target?.value
    if (val == null || val === '') {
      this.selectedEinsatzortGemeinde = null
      this.refreshBerufeForSelection()
      return
    }
    const item = this.gemeindeItems.find(i => i.value === val)
    this.selectedEinsatzortGemeinde = item
      ? { plz: item.plz, kanton: item.kanton, ort: item.ort }
      : null
    this.refreshBerufeForSelection()
  }

  async handleGemeindeSearch(searchTerm) {
    if (!searchTerm || searchTerm.length < 2) {
      this.gemeindeItems = []
      this.selectedEinsatzortGemeinde = null
      this.render()
      return
    }

    try {
      const results = await new GemeindeService().search(searchTerm)
      if (results && results.length > 0) {
        this.gemeindeItems = results.map(g => ({
          value: gemeindeOptionValue(g),
          name: formatGemeindeDropdownLabel(g),
          plz: g.plz,
          kanton: g.kanton,
          ort: g.ort
        }))
      } else {
        this.gemeindeItems = []
        this.selectedEinsatzortGemeinde = null
      }
    } catch {
      this.gemeindeItems = []
      this.selectedEinsatzortGemeinde = null
    }
    this.render()
  }

  getEndeMinDate(defaultMinDate) {
    if (!this.selectedBeginn) {
      return defaultMinDate
    }
    return this.selectedBeginn > defaultMinDate ? this.selectedBeginn : defaultMinDate
  }

  handleBeginnChange(e) {
    const val = e.detail?.value ?? e.target?.value ?? ''
    this.selectedBeginn = val

    const defaultMinDate = toIsoDate(todayPlusDays(MIN_DAYS_BEFORE_BEGINN))
    const endeMinDate = this.getEndeMinDate(defaultMinDate)
    const endeInput = this.querySelector('isceco-date-input[name="ende"]')
    const endeValue = endeInput?.value ?? endeInput?.getAttribute('value') ?? ''

    if (endeInput && endeValue && endeValue < endeMinDate) {
      endeInput.value = ''
      endeInput.setAttribute('value', '')
      endeInput.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
      endeInput.dispatchEvent(new Event('change', { bubbles: true, composed: true }))
    }

    this.refreshBerufeForSelection()
    this.render()
  }

  validateInputs({ einsatzort, beginn, ende }) {
    const errorTitle = this.i18n.translate(ERROR_TITLE_I18N_KEY)

    if (!this.selectedEinsatzortGemeinde || gemeindeOptionValue(this.selectedEinsatzortGemeinde) !== einsatzort) {
      showAlert(errorTitle, this.i18n.translate('mindestlohnrechner.validation.einsatzortAuswahl'), 'error')
      return false
    }

    const minBeginn = todayPlusDays(MIN_DAYS_BEFORE_BEGINN)
    const beginnDate = new Date(beginn)
    if (!beginn || Number.isNaN(beginnDate.getTime()) || beginnDate < minBeginn) {
      showAlert(errorTitle, this.i18n.translate('mindestlohnrechner.validation.beginnMin8Tage'), 'error')
      return false
    }

    const endeDate = new Date(ende)
    if (!ende || Number.isNaN(endeDate.getTime()) || endeDate < beginnDate) {
      showAlert(errorTitle, this.i18n.translate('mindestlohnrechner.validation.endeNachBeginn'), 'error')
      return false
    }

    return true
  }

   async handleSuchen() {
    const values = this.querySelector('#mindestlohnrechner-form').getValues()
    if (!this.validateInputs(values)) { return }

    this.captureFormValues(values)

    const { kanton } = this.selectedEinsatzortGemeinde
    const selectedBerufItem = this.berufItems.find(item => item.value === values.beruf)
    this.lastRequestData = this.buildSearchRequestData(values, selectedBerufItem)
    this.lastSearchInput = this.buildSearchInput(values, selectedBerufItem)

    this.isLoading = true
    this.render()

    try {
      const result = await new MindestlohnService().search(this.lastRequestData)
      this.applySearchResult(result)
      await this.loadContactsByKanton(kanton)
    } finally {
      this.isLoading = false
      this.render()
      if (this.hasSearched) {
        requestAnimationFrame(() => {
          this.scrollToResultSectionStart()
        })
      }
    }
  }

  captureFormValues(values) {
    this.formValues = {
      einsatzort: values.einsatzort || '',
      beginn: values.beginn || '',
      ende: values.ende || '',
      beruf: values.beruf || ''
    }
  }

  resolveBerufIds(values, selectedBerufItem) {
    let resolvedBerufIds = []
    if (Array.isArray(selectedBerufItem?.berufIds) && selectedBerufItem.berufIds.length > 0) {
      resolvedBerufIds = selectedBerufItem.berufIds
    } else if (values.beruf) {
      resolvedBerufIds = [Number.parseInt(values.beruf)]
    }
    return resolvedBerufIds.filter(berufId => Number.isFinite(berufId) && berufId > 0)
  }

  buildSearchRequestData(values, selectedBerufItem) {
    const { plz, kanton, ort } = this.selectedEinsatzortGemeinde
    return {
      einsatzortPlz: plz,
      einsatzortKanton: kanton,
      einsatzortOrt: ort,
      beginn: values.beginn,
      ende: values.ende,
      berufIds: this.resolveBerufIds(values, selectedBerufItem),
      language: getLanguage()
    }
  }

  buildSearchInput(values, selectedBerufItem) {
    return {
      einsatzortLabel: this.gemeindeItems.find(i => i.value === values.einsatzort)?.name || values.einsatzort,
      beginn: values.beginn,
      ende: values.ende,
      suchbegriff: selectedBerufItem?.name || ''
    }
  }

  applySearchResult(result) {
    this.resultTreffer = Array.isArray(result) ? result : []
    this.hasSearched = true
    this.selectedTrefferKey = this.resultTreffer.length > 0 ? this.getTrefferKey(this.resultTreffer[0]) : 'NO_MATCH'
    this.expandedSections = {}
    this.activeResultPage = null
    this.selectedContractMeta = null
    this.selectedContractNumber = null
    this.criteriaFields = []
    this.currentSelections = {}
    this.resultRow = null
    this.resultHash = null
    this.expandedHintSections = {}
    this.contractParitaetContacts = []
  }

  async loadContactsByKanton(kanton) {
    if (!kanton) {
      this.noMatchContacts = []
      return
    }
    try {
      const migrationArbeitsmarkt = await new KontaktinformationService().findByKanton(kanton, 0)
      this.noMatchContacts = Array.isArray(migrationArbeitsmarkt) ? migrationArbeitsmarkt : []
    } catch {
      this.noMatchContacts = []
    }
  }

  getTrefferKey(treffer) {
    return `${treffer.contractNumber}|${treffer.typ || ''}`
  }

  toggleSection(sectionKey) {
    this.expandedSections = {
      ...this.expandedSections,
      [sectionKey]: !this.expandedSections[sectionKey]
    }
    this.render()
  }

  selectTreffer(key) {
    this.selectedTrefferKey = key
    this.render()
  }

  getSelectedTreffer() {
    if (!this.resultTreffer || this.resultTreffer.length === 0) {
      return null
    }
    if (!this.selectedTrefferKey || this.selectedTrefferKey === 'NO_MATCH') {
      return null
    }
    return this.resultTreffer.find(treffer => this.getTrefferKey(treffer) === this.selectedTrefferKey) || null
  }

  getPageTitleKey(page) {
    return getResultPageTitleKey(page)
  }

  getSelectedContractDisplayName() {
    const meta = this.selectedContractMeta || this.getSelectedTreffer()
    if (!meta) {
      return ''
    }
    return resolveTrefferName(meta, getLanguage())
  }

  getActiveResultPageTitle() {
    return formatResultPageTitle(
      this.activeResultPage,
      key => this.i18n.translate(key),
      this.getSelectedContractDisplayName()
    )
  }

  renderTemplate() {
    if (this.css) {
      render(this.getTemplateWithCss(), this.root)
    } else {
      render(this.getTemplate(), this.root)
    }
    this.syncStickyResultNavigation()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.clearStickyResultNavigation()
  }

  syncStickyResultNavigation() {
    const host = document.getElementById('result-nav-host')
    if (!host) {
      return
    }
    if (!this.activeResultPage) {
      this.clearStickyResultNavigation()
      return
    }
    host.hidden = false
    render(renderResultNavigationBar(this), host)
  }

  clearStickyResultNavigation() {
    const host = document.getElementById('result-nav-host')
    if (!host) {
      return
    }
    host.hidden = true
    render(html``, host)
  }

  getStickyHeaderOffset() {
    const header = document.getElementById('mainheader')
    return (header?.getBoundingClientRect().height || 80) + 16
  }

  async openResultPage(page) {
    if (page === RESULT_PAGE.NO_MATCH) {
      this.activeResultPage = RESULT_PAGE.NO_MATCH
      this.render()
      requestAnimationFrame(() => {
        this.scrollToPageTitle()
      })
      return
    }

    const selectedTreffer = this.getSelectedTreffer()
    if (!selectedTreffer) {
      showAlert(
        this.i18n.translate(ERROR_TITLE_I18N_KEY),
        this.i18n.translate(DETAILS_VALIDATION_SELECT_CONTRACT_I18N_KEY),
        'error'
      )
      return
    }

    this.selectedContractMeta = selectedTreffer
    await this.loadContractParitaetContacts(selectedTreffer.typ, selectedTreffer.contractNumber)

    if (page === RESULT_PAGE.MINDESTLOEHNE && this.selectedContractNumber !== selectedTreffer.contractNumber) {
      try {
        await this.loadCriteria(selectedTreffer.contractNumber, {})
      } catch {
        return
      }
    }

    this.activeResultPage = page
    this.render()
    requestAnimationFrame(() => {
      this.scrollToPageTitle()
    })
  }

  async loadCriteria(contractNumber, selections) {
    if (!this.lastRequestData) {
      return
    }
    this.isLoadingCriteria = true
    this.render()
    const request = {
      ...this.lastRequestData,
      contractNumber,
      selections: selections || {},
      language: getLanguage()
    }
    try {
      const step = await new MindestlohnService().criteria(request)
      this.criteriaFields = Array.isArray(step?.fields) ? step.fields : []
      this.selectedContractNumber = contractNumber
      this.currentSelections = this.initializeSelections(this.criteriaFields)
      this.resultHash = step?.resultReady ? (step.resultHash || null) : null
      this.expandedHintSections = {}
      await this.loadResult()
    } finally {
      this.isLoadingCriteria = false
      this.render()
    }
  }

  async loadResult() {
    if (!this.resultHash || !this.selectedContractNumber || !this.lastRequestData) {
      this.resultRow = null
      return
    }
    this.isLoadingResult = true
    this.render()
    const request = {
      ...this.lastRequestData,
      contractNumber: this.selectedContractNumber,
      resultHash: this.resultHash,
      language: getLanguage()
    }
    try {
      this.resultRow = await new MindestlohnService().result(request)
    } catch {
      this.resultRow = null
    } finally {
      this.isLoadingResult = false
      this.render()
    }
  }

  async loadContractParitaetContacts(typ, contractNumber) {
    const type = typ || this.getSelectedTreffer()?.typ
    if (type === 'NAV' || !contractNumber) {
      this.contractParitaetContacts = []
      return
    }
    try {
      const contacts = await new KontaktinformationService().findByGav(contractNumber)
      this.contractParitaetContacts = Array.isArray(contacts) ? contacts : []
    } catch {
      this.contractParitaetContacts = []
    }
  }

  initializeSelections(fields) {
    const selections = {}
    if (Array.isArray(fields)) {
      fields.forEach(field => {
        selections[String(field.criteriaStructureId)] = field.selectedValue || ''
      })
    }
    return selections
  }

  async updateEmployeeSelection(criteriaStructureId, value) {
    if (!this.isEmployeeFieldEnabled(criteriaStructureId)) {
      return
    }
    const nextSelections = {
      ...this.currentSelections,
      [String(criteriaStructureId)]: value || ''
    }
    this.currentSelections = nextSelections
    if (!this.selectedContractNumber) {
      this.render()
      return
    }
    await this.loadCriteria(this.selectedContractNumber, nextSelections)
  }

  toggleHintSection(sectionId) {
    this.expandedHintSections = {
      ...this.expandedHintSections,
      [sectionId]: !this.expandedHintSections[sectionId]
    }
    this.render()
  }

  handleHintContentClick(event) {
    const link = event.target?.closest?.('a')
    if (!link) {
      return
    }
    const href = link.getAttribute('href')
    if (href === '#result-gav-kurz') {
      event.preventDefault()
      this.openResultPage(RESULT_PAGE.GAV_KURZ)
      return
    }
    if (href === '#result-kontakte') {
      event.preventDefault()
      this.openResultPage(RESULT_PAGE.KONTAKTE)
      return
    }
    if (href === '#export-excel') {
      event.preventDefault()
      this.handleExcelExport()
    }
  }

  scrollToResultSectionStart() {
    const resultSection = this.querySelector('.result-section')
    if (!resultSection) {
      return
    }
    const heading = resultSection.querySelector('h2')
    const top = (heading || resultSection).getBoundingClientRect().top + window.scrollY - this.getStickyHeaderOffset()
    window.scrollTo({ top, behavior: 'smooth' })
  }

  scrollToPageTitle() {
    const pageTitle = this.querySelector('#page-title')
    if (!pageTitle) {
      return
    }
    const top = pageTitle.getBoundingClientRect().top + window.scrollY - this.getStickyHeaderOffset()
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
  }

  backToSelection() {
    this.activeResultPage = null
    this.expandedSections = {}
    this.render()
    requestAnimationFrame(() => {
      this.scrollToPageTitle()
    })
  }

  getMatchingDetailRow() {
    if (this.isLoadingCriteria || this.isLoadingResult) {
      return null
    }
    return this.resultRow || null
  }

  getVisibleEmployeeFields() {
    if (!Array.isArray(this.criteriaFields)) {
      return []
    }
    return this.criteriaFields
      .filter(field => !field.hidden)
      .sort((left, right) => Number(left.sortOrder ?? 0) - Number(right.sortOrder ?? 0))
  }

  isEmployeeFieldEnabled() {
    // The cascade is computed server-side: only reachable fields are returned (non-hidden),
    // so every rendered field is selectable except while a step is loading.
    return !this.isLoadingCriteria && !this.isLoadingResult
  }

  isSalaryTableReady() {
    if (this.isLoadingCriteria || this.isLoadingResult) {
      return false
    }
    return Boolean(this.resultRow)
  }

  formatCurrency(value) {
    if (value == null || Number.isNaN(Number(value))) {
      return '-'
    }
    return `${Number(value).toFixed(2)} CHF`
  }

  async handleExcelExport() {
    if (this.isLoadingCriteria || this.isLoadingResult || !this.selectedContractNumber || !this.lastRequestData) {
      showAlert(
        this.i18n.translate(ERROR_TITLE_I18N_KEY),
        this.i18n.translate(DETAILS_VALIDATION_SELECT_CONTRACT_I18N_KEY),
        'error'
      )
      return
    }
    if (!this.isSalaryTableReady() || !this.resultHash) {
      showAlert(
        this.i18n.translate(ERROR_TITLE_I18N_KEY),
        this.i18n.translate(DETAILS_SALARY_WAIT_FOR_SELECTION_I18N_KEY),
        'error'
      )
      return
    }
    const request = {
      ...this.lastRequestData,
      contractNumber: this.selectedContractNumber,
      selections: this.currentSelections,
      language: getLanguage(),
      resultHash: this.resultHash
    }
    await new MindestlohnService().detailsExcel(request)
  }

  isBusyLoading() {
    return this.isLoadingCriteria || this.isLoadingResult || this.isPdfLoading
  }

  canExportPdf() {
    if (!this.activeResultPage || this.activeResultPage === RESULT_PAGE.NO_MATCH) {
      return false
    }
    if (!this.lastRequestData || !this.selectedContractMeta || this.isBusyLoading()) {
      return false
    }
    if (this.activeResultPage === RESULT_PAGE.MINDESTLOEHNE) {
      return this.isSalaryTableReady()
    }
    return Boolean(this.selectedContractMeta)
  }

  async handlePdfExport() {
    if (!this.canExportPdf()) {
      showAlert(
        this.i18n.translate(ERROR_TITLE_I18N_KEY),
        this.i18n.translate(DETAILS_VALIDATION_SELECT_CONTRACT_I18N_KEY),
        'error'
      )
      return
    }
    const request = {
      ...this.lastRequestData,
      contractNumber: this.selectedContractNumber || this.selectedContractMeta?.contractNumber,
      selections: this.currentSelections,
      language: getLanguage(),
      resultHash: this.resultHash || ''
    }
    const now = new Date()
    const pdfRequest = {
      request,
      pageTitle: this.getActiveResultPageTitle(),
      generatedAtLabel: this.i18n.translate('mindestlohnrechner.details.pdf.generatedAtLabel'),
      generatedAtValue: formatDateForPdf(toIsoDate(now)),
      sections: this.buildPdfSections()
    }
    this.isPdfLoading = true
    this.render()
    try {
      await new MindestlohnService().downloadPdf(pdfRequest)
    } finally {
      this.isPdfLoading = false
      this.render()
    }
  }

  buildPdfSections() {
    return buildResultPdfSections(this, {
      detailsHintsTitleKey: DETAILS_HINTS_TITLE_I18N_KEY,
      detailsHintPersonalverleihTitleKey: DETAILS_HINT_PERSONALVERLEIH_TITLE_I18N_KEY,
      detailsHintPersonalverleihTextKey: DETAILS_HINT_PERSONALVERLEIH_TEXT_I18N_KEY,
      detailsHintRechtlicherHinweisTitleKey: DETAILS_HINT_RECHTLICHER_HINWEIS_TITLE_I18N_KEY,
      detailsHintRechtlicherHinweisTextKey: DETAILS_HINT_RECHTLICHER_HINWEIS_TEXT_I18N_KEY,
      detailsTitleGavKurzKey: DETAILS_TITLE_GAV_KURZ_I18N_KEY
    })
  }

  getGavKurzSummaryGroups() {
    return buildGavKurzSummaryGroups(this.selectedContractMeta, value => toPlainText(value))
  }

  get css() {
    return mindestlohnrechnerStyles
  }

  getTemplate() {
    const showSearchContext = !this.activeResultPage
    const pageTitle = this.activeResultPage
      ? this.getActiveResultPageTitle()
      : this.i18n.translate('page.title')
    return html`
      <div class="page-wrapper">
        <isceco-title id="page-title" text="${pageTitle}"></isceco-title>

        ${showSearchContext
          ? html`
            <p class="intro-text">${this.i18n.translate('mindestlohnrechner.intro')}</p>

            <isceco-form
              id="mindestlohnrechner-form"
              submit-button-text="${this.i18n.translate('mindestlohnrechner.button.suchen')}"
              ?disabled="${this.isLoading}"
              .value="${this.formValues}"
              @submit="${() => this.handleSuchen()}"
            >
              <div slot="form-elements">
                ${this.renderEinsatzort()}
                ${this.renderDateInput('beginn', 'mindestlohnrechner.beginn')}
                ${this.renderDateInput('ende', 'mindestlohnrechner.ende')}

                <p class="beruf-hint">${this.i18n.translate('mindestlohnrechner.beruf.hint')}</p>

                <div class="field-wrapper">
                  <div class="field-label">
                    <span class="field-label-text">${this.i18n.translate('mindestlohnrechner.beruf.title')}</span>
                  </div>
                  <isceco-dropdown
                    name="beruf"
                    .items="${this.berufItems}"
                    searchable
                  ></isceco-dropdown>
                </div>
              </div>
            </isceco-form>
          `
          : ''}

        ${this.hasSearched ? this.renderResult() : ''}

        ${renderRelevantLinks(this)}
      </div>
    `
  }

  renderEinsatzort() {
    return html`
      <div class="field-wrapper">
        <div class="field-label">
          <span class="field-label-text">${this.i18n.translate('mindestlohnrechner.einsatzort.title')}</span>
          <isceco-popup direction="right">
            <span slot="wrapper" class="info-icon">i</span>
            <div slot="content" class="popup-content">
              ${this.i18n.translate('mindestlohnrechner.einsatzort.popup')}
            </div>
          </isceco-popup>
        </div>
        <isceco-dropdown
          name="einsatzort"
          placeholder="${this.i18n.translate('mindestlohnrechner.einsatzort.placeholder')}"
          .items="${this.gemeindeItems}"
          searchable
          required
          @change="${e => this.handleEinsatzortChange(e)}"
          @input="${e => {
            const dropdown = e.target
            const shadowRoot = dropdown.shadowRoot
            const searchInput = shadowRoot?.querySelector('input[type="text"]') || shadowRoot?.querySelector('input')
            const searchValue = searchInput?.value || ''
            if (searchValue.length >= 2 || searchValue.length === 0) {
              this.handleGemeindeSearch(searchValue)
            }
          }}"
        ></isceco-dropdown>
      </div>
    `
  }

  renderDateInput(name, i18nBase) {
    const defaultMinDate = toIsoDate(todayPlusDays(MIN_DAYS_BEFORE_BEGINN))
    const minDate = name === 'ende' ? this.getEndeMinDate(defaultMinDate) : defaultMinDate
    return html`
      <div class="field-wrapper">
        <div class="field-label">
          <span class="field-label-text">${this.i18n.translate(`${i18nBase}.title`)}</span>
          <isceco-popup direction="right">
            <span slot="wrapper" class="info-icon">i</span>
            <div slot="content" class="popup-content">
              ${this.i18n.translate(`${i18nBase}.popup`)}
            </div>
          </isceco-popup>
        </div>
        <isceco-date-input
          name="${name}"
          type="date"
          min="${minDate}"
          open-on-click
          required
          @input="${e => {
            if (name === 'beginn') {
              this.handleBeginnChange(e)
            }
          }}"
          @change="${e => {
            if (name === 'beginn') {
              this.handleBeginnChange(e)
            }
          }}"
        ></isceco-date-input>
      </div>
    `
  }

  renderResult() {
    return renderResultSection(this)
  }
}

customElements.define('mindestlohn-frontend-mindestlohnrechner', Mindestlohnrechner)
